import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { ExplanationAccordion, GreenText, LabelField } from 'components'
import * as controllers from 'controllers'
import { VehicleDto } from 'dtos'
import { useEffect, useState } from 'react'
import TirePressureLabelEditDialog from './TirePressureLabelEditDialog'
import { useAuth } from 'context'
import { VehicleCheckInTirePressureLabelDatesDto } from 'dtos/vehicleCheckInTirePressureLabelDatesDto'
import { enqueueSnackbar } from 'notistack'
import { formatDate } from 'utils'

interface TirePressureLabelCardProps {
  vehicle: VehicleDto
}

export default function ExporterInformationListCard({ vehicle }: TirePressureLabelCardProps) {
  const { permissions } = useAuth()

  const [isSavingTirePressureLabelDates, setIsSavingTirePressureLabelDates] = useState<boolean>(false)
  const [isGettingTirePressureLabelDates, setIsGettingTirePressureLabelDates] = useState<boolean>(false)
  const [tirePressureLabelEditDialogOpen, setTirePressureLabelEditDialogOpen] = useState<boolean>(false)
  const [tirePressureLabelDates, setTirePressureLabelDates] = useState<VehicleCheckInTirePressureLabelDatesDto>(
    new VehicleCheckInTirePressureLabelDatesDto()
  )

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      setIsGettingTirePressureLabelDates(true)
      controllers
        .getTirePressureLabelDateInformation(vehicle.vehicleGuid)
        .then(response => {
          setTirePressureLabelDates(response.value)
        })
        .finally(() => {
          setIsGettingTirePressureLabelDates(false)
        })
    }
  }, [vehicle])

  return (
    <>
      <TirePressureLabelEditDialog
        initialValues={tirePressureLabelDates}
        isLoading={isSavingTirePressureLabelDates}
        onClose={() => {
          setTirePressureLabelEditDialogOpen(false)
        }}
        onSave={values => {
          setIsSavingTirePressureLabelDates(true)
          controllers
            .updateTirePressureLabelDateInformation(vehicle.vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Tire Pressure Label Dates Saved Successfully!', { variant: 'success' })
              setTirePressureLabelDates(response.value)
              setTirePressureLabelEditDialogOpen(false)
            })
            .finally(() => {
              setIsSavingTirePressureLabelDates(false)
            })
        }}
        open={tirePressureLabelEditDialogOpen}
      />

      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={6}>
                <Typography fontWeight='bold' variant='h5'>
                  Tire Pressure Label
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  disabled={
                    permissions.VEHICLE_CHECK_IN_EDIT === false ||
                    tirePressureLabelDates.vehicleCheckInIsTirePressureLabelPhotoMissing !== true
                  }
                  onClick={() => {
                    setTirePressureLabelEditDialogOpen(true)
                  }}
                  variant='text'
                  endIcon={<Edit />}
                >
                  EDIT
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingTirePressureLabelDates && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingTirePressureLabelDates && (
              <>
                <Grid item xs={12}>
                  <LabelField
                    label='Label Ordered'
                    value={
                      tirePressureLabelDates.tirePressureLabelOrderedDate
                        ? formatDate(tirePressureLabelDates.tirePressureLabelOrderedDate)
                        : ''
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Label Received'
                    value={
                      tirePressureLabelDates.tirePressureLabelReceivedDate
                        ? formatDate(tirePressureLabelDates.tirePressureLabelReceivedDate)
                        : ''
                    }
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**EDIT Button:**

Clicking the EDIT button opens the Tire Label dialog.

The Tire Label section is only editable if the vehicle has been 
checked in and the Tire Label Missing button was pressed 
during the vehicle check-in process.

**Label Order:**

Displays the date when the Label was ordered, as entered in 
the Label dialog.

If no value has been entered for the Label Received, a “-” will 
be displayed.

**Label Received:**

Displays the date when the Label was received, as entered in 
the Label dialog.

If no value has been entered for the Label Ordered, a “-” will 
be displayed.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
